import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ChevronDown, FileText, PenLine, Sparkles, X } from "lucide-react";
import { CopyButton } from "../ui/CopyButton";
import { cn } from "../../utils/cn";
import { workflowErrorMessage } from "./workflowErrorMessage";
import { askCopilot } from "../../services/helpdeskWorkflowService";

// ─── Copiloto IA ─────────────────────────────────────────────────────────────
// Dos pedidos al agente IA de la conversación, sin efectos laterales:
//   · "Resumir caso"     → el resumen va a un panel descartable sobre el editor;
//   · "Sugerir respuesta" → el borrador va al editor de respuesta, marcado como
//     borrador. NUNCA se envía solo: la persona lo revisa y envía.

/** Texto del error en lenguaje del agente. Exportada para test. */
export function copilotErrorMessage(err, t) {
  if (err?.status === 409) return t("La IA está apagada o no hay un agente para esta conversación");
  if (err?.status === 429) return t("Se agotó el presupuesto diario de la IA");
  return workflowErrorMessage(err, t("El copiloto no respondió. Inténtalo de nuevo."));
}

/**
 * Estado del copiloto de UNA conversación. Una respuesta que llega después de
 * cambiar de conversación se descarta.
 */
export function useCopilot(conversationId) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(null); // 'summary' | 'reply' | null
  const [summary, setSummary] = useState(null); // {text, agent_name} | null
  const [error, setError] = useState(null);
  const requestRef = useRef(0);

  useEffect(() => {
    requestRef.current += 1;
    setLoading(null);
    setSummary(null);
    setError(null);
  }, [conversationId]);

  /** @returns {Promise<string|null>} texto obtenido, o null si falló o quedó obsoleto */
  const ask = useCallback(
    async (mode) => {
      if (!conversationId) return null;
      const requestId = ++requestRef.current;
      setLoading(mode);
      setError(null);
      try {
        const data = await askCopilot(conversationId, mode);
        if (requestId !== requestRef.current) return null;
        const text = String(data?.text || "").trim();
        if (!text) {
          setError(t("El copiloto no devolvió texto. Inténtalo de nuevo."));
          return null;
        }
        if (mode === "summary") setSummary({ text, agent_name: data?.agent_name || null });
        return text;
      } catch (err) {
        if (requestId !== requestRef.current) return null;
        setError(copilotErrorMessage(err, t));
        return null;
      } finally {
        if (requestId === requestRef.current) setLoading(null);
      }
    },
    [conversationId, t],
  );

  const dismiss = useCallback(() => {
    setSummary(null);
    setError(null);
  }, []);

  return { loading, summary, error, ask, dismiss };
}

/** Botón "Copiloto" con sus dos acciones. */
export function CopilotMenu({ loading, onSummary, onReply, replyDisabledReason }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const busy = !!loading;
  const item =
    "w-full flex items-center gap-2 px-3 min-h-[36px] text-xs text-fg hover:bg-bg-muted text-left disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          "inline-flex items-center gap-1.5 h-[36px] md:h-[28px] px-[8px] rounded-md text-[12px] font-medium whitespace-nowrap transition-colors disabled:opacity-60",
          open ? "bg-bg-subtle text-fg" : "text-fg-muted hover:bg-bg-muted hover:text-fg",
        )}
      >
        {busy ? (
          <span
            className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
        ) : (
          <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
        )}
        {busy
          ? loading === "summary"
            ? t("Resumiendo…")
            : t("Redactando…")
          : t("Copiloto")}
        {!busy && <ChevronDown className="w-3 h-3" aria-hidden="true" />}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 bottom-full mb-1 min-w-[200px] z-30 bg-bg border border-border rounded-md shadow-elevated py-1 animate-scale-in origin-bottom-right motion-reduce:animate-none"
        >
          <button
            type="button"
            role="menuitem"
            className={item}
            onClick={() => {
              setOpen(false);
              onSummary();
            }}
          >
            <FileText className="w-3.5 h-3.5 text-fg-muted shrink-0" aria-hidden="true" />
            {t("Resumir caso")}
          </button>
          <button
            type="button"
            role="menuitem"
            className={item}
            disabled={!!replyDisabledReason}
            title={replyDisabledReason || undefined}
            onClick={() => {
              setOpen(false);
              onReply();
            }}
          >
            <PenLine className="w-3.5 h-3.5 text-fg-muted shrink-0" aria-hidden="true" />
            {t("Sugerir respuesta")}
          </button>
        </div>
      )}
    </div>
  );
}

/** Panel sobre el editor: resumen del caso o error del copiloto. */
export function CopilotPanel({ summary, error, onDismiss }) {
  const { t } = useTranslation();
  if (!summary && !error) return null;
  if (error) {
    return (
      <div
        role="alert"
        className="mx-[8px] mt-[8px] px-[10px] py-[6px] rounded-lg text-[12px] bg-status-error-soft text-status-error flex items-start gap-2"
      >
        <AlertTriangle className="w-4 h-4 shrink-0 mt-px" aria-hidden="true" />
        <span className="flex-1">{error}</span>
        <button
          type="button"
          onClick={onDismiss}
          className="p-0.5 rounded hover:bg-status-error/10"
          aria-label={t("Cerrar")}
        >
          <X className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </div>
    );
  }
  return (
    <section
      aria-label={t("Resumen del caso")}
      // Neutral surface inside the composer card; the sparkle icon is the
      // only accent (second visual pass: no peach panel).
      className="mx-[8px] mt-[8px] rounded-lg bg-bg-subtle"
    >
      <header className="flex items-center gap-2 pl-[12px] pr-[4px] pt-[4px]">
        <Sparkles className="w-3.5 h-3.5 text-brand-700 shrink-0" aria-hidden="true" />
        <h3 className="flex-1 min-w-0 text-[12px] font-semibold text-fg truncate">
          {t("Resumen del caso")}
          {summary.agent_name && (
            <span className="font-normal text-fg-muted">
              {" · "}
              {summary.agent_name}
            </span>
          )}
        </h3>
        <CopyButton
          value={summary.text}
          label={t("Copiar resumen")}
          copiedMessage={t("Resumen copiado")}
          className="w-7 h-7 justify-center m-0"
        />
        <button
          type="button"
          onClick={onDismiss}
          className="inline-flex w-7 h-7 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg"
          aria-label={t("Cerrar resumen")}
        >
          <X className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </header>
      <p className="px-[12px] pb-[10px] pt-[2px] max-h-40 overflow-y-auto text-[13px] leading-[20px] text-fg whitespace-pre-wrap">
        {summary.text}
      </p>
    </section>
  );
}
